
import { useEffect, useState, useCallback } from 'react';
// Capacitor-free platform detection utilities
const getPlatformName = (): string => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  return 'web';
};
const isNativePlatform = (): boolean => {
  return typeof (window as any).Capacitor !== 'undefined';
};
import { NATIVE_CONFIG } from '@/config/nativeConfig';

export function useNativeFeatures() {
  const [isNative, setIsNative] = useState(false);
  const [supportedFeatures, setSupportedFeatures] = useState({
    share: false,
    camera: false,
    notifications: false,
    deepLinks: false,
    haptics: false,
    biometrics: false
  });
  
  // Mobile authentication state
  const [authState, setAuthState] = useState({
    deeplinkHandlerReady: false,
    handlingAuthRedirect: false,
    lastAuthError: null as Error | null
  });
  
  useEffect(() => {
    // Check if running on a native platform
    const native = isNativePlatform();
    setIsNative(native);
    
    // Check for available native features
    const checkSupportedFeatures = () => {
      setSupportedFeatures({
        share: 'share' in navigator,
        camera: 'mediaDevices' in navigator,
        notifications: 'Notification' in window,
        deepLinks: native, // Simplification, in a real app you'd check for the Capacitor App plugin
        haptics: native, // Simplification, in a real app you'd check for the Capacitor Haptics plugin
        biometrics: native // Simplification, in a real app you'd check for a biometrics plugin
      });
    };
    
    checkSupportedFeatures();
    
    // Setup deep link handlers for auth on native platforms
    if (native) {
      setupDeepLinkHandlers();
    }
  }, []);
  
  const setupDeepLinkHandlers = async () => {
    if (!isNative) return;
    
    try {
      // In a real implementation, you would use the App plugin from Capacitor
      // This is a placeholder implementation
      console.log('Setting up deep link handlers for:', NATIVE_CONFIG.deepLink.scheme);
      
      // Placeholder for Capacitor App plugin deep link handler
      /*
      App.addListener('appUrlOpen', (data: { url: string }) => {
        const url = data.url;
        console.log('Deep link received:', url);
        
        // Check if this is an auth callback
        if (url.includes('auth/callback') || url.includes('login/callback')) {
          setAuthState(prev => ({ ...prev, handlingAuthRedirect: true }));
          handleAuthCallback(url);
        }
      });
      */
      
      setAuthState(prev => ({ ...prev, deeplinkHandlerReady: true }));
    } catch (e) {
      console.error('Failed to setup deep link handlers:', e);
      setAuthState(prev => ({ 
        ...prev, 
        deeplinkHandlerReady: false,
        lastAuthError: e instanceof Error ? e : new Error(String(e))
      }));
    }
  };
  
  const handleAuthCallback = async (url: string) => {
    try {
      console.log('Processing auth callback from deeplink:', url);
      
      // Extract authentication parameters from URL
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.hash.substring(1));
      
      // Check for errors first
      const error = params.get('error');
      if (error) {
        const errorDescription = params.get('error_description');
        throw new Error(`Auth error: ${error} - ${errorDescription}`);
      }
      
      // Process successful authentication
      const accessToken = params.get('access_token');
      const idToken = params.get('id_token');
      
      if (!accessToken || !idToken) {
        throw new Error('Invalid tokens received from auth provider');
      }
      
      console.log('Successfully received tokens from auth provider');
      
      // At this point, you would typically store tokens and update auth state
      // For authentication integration, this might involve manually updating the auth context
      
      setAuthState(prev => ({ ...prev, handlingAuthRedirect: false }));
    } catch (e) {
      console.error('Error handling auth callback:', e);
      setAuthState(prev => ({ 
        ...prev, 
        handlingAuthRedirect: false,
        lastAuthError: e instanceof Error ? e : new Error(String(e))
      }));
    }
  };

  const getPlatformInfo = () => {
    const platform = getPlatformName();
    return {
      isNative,
      platform,
      isIOS: platform === 'ios',
      isAndroid: platform === 'android',
      isWeb: platform === 'web',
      authScheme: isNative ? NATIVE_CONFIG.deepLink.scheme : 'https'
    };
  };
  
  const checkNetworkStatus = () => {
    return {
      online: navigator.onLine,
      // The Navigator.connection API is not standard and needs type checking
      connectionType: getConnectionType()
    };
  };

  // Helper function to safely check connection type
  const getConnectionType = (): string => {
    // Check if the connection property exists on navigator
    if ('connection' in navigator) {
      const conn = (navigator as any).connection;
      if (conn && 'effectiveType' in conn) {
        return conn.effectiveType;
      }
    }
    return 'unknown';
  };
  
  const vibrate = (pattern: number | number[] = 100) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
      return true;
    }
    return false;
  };
  
  // Generate platform-specific redirect URI for mobile apps
  const getMobileRedirectUri = useCallback(() => {
    const platformInfo = getPlatformInfo();
    
    if (!isNative) {
      return window.location.origin + NATIVE_CONFIG.redirects.auth;
    }
    
    if (platformInfo.isIOS) {
      return `${NATIVE_CONFIG.deepLink.scheme}://${NATIVE_CONFIG.redirects.auth}`;
    }
    
    if (platformInfo.isAndroid) {
      return `${NATIVE_CONFIG.deepLink.scheme}://${NATIVE_CONFIG.redirects.auth}`;
    }
    
    // Fallback to web URI if platform can't be determined
    return window.location.origin + NATIVE_CONFIG.redirects.auth;
  }, [isNative]);
  
  // Get platform-appropriate configuration for login
  const getAuthConfig = useCallback(() => {
    const baseConfig = { ...NATIVE_CONFIG };
    
    if (isNative) {
      return {
        ...baseConfig,
        redirectUri: getMobileRedirectUri()
      };
    }
    
    return baseConfig;
  }, [isNative, getMobileRedirectUri]);

  return {
    isNative,
    ...getPlatformInfo(),
    supportedFeatures,
    checkNetworkStatus,
    vibrate,
    auth: {
      ...authState,
      getConfig: getAuthConfig,
      getMobileRedirectUri
    }
  };
}
